import { Component, OnDestroy, OnInit } from '@angular/core';
import { ReservationService } from '../../reservation.service';
import {
	DocumentKind,
	DocumentToSend,
	Offer,
	Payment,
	PaymentsTerm,
	Reservation,
	Slot,
	Document,
	DocumentList,
	DocumentConvertedTransport,
	Location,
	ApiDate,
	DocumentSlotTransport,
	SortedTermObj,
} from '../../reservation.model';
import { DataBagService } from '@/common/data-bag.service';
import { environment } from '../../../../environments/environment';
import { DateService } from '@/common/date.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { ReservationSummaryApiService } from '../services/reservation-summary-api.service';
import { ReservationSummaryStoreService } from '../services/reservation-summary-store.service';
import { LocationKinds } from '@/common/enums/LocationKinds';
import { AddressKind } from '@/common/enums/AddressKind';
import { LoaderService } from '@/common/loader/loader.service';
import { PricePipe } from '@/common/pipes/price.pipe';
import { CancellationStatuses, Status, statusMappings } from '@/common/enums/Status';
import { DatePipe } from '@angular/common';
import { PaymentStatus, PaymentTermStatus } from 'src/app/common/enums/paymentStatus';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { DocumentDownloader } from './document-downloader';
import { RequestTrackerInterceptor } from '@/common/interceptors/request-tracker.interceptor';
import { RequestTrackerService } from '@/common/request-tracker.service';
import { DateBoundsPipe } from '@/common/pipes/date-bounds.pipe';
import { EmailType } from '@/common/enums/EmailType';
import { DocumentName } from '@/common/enums/DocumentName';
import { TransportStopType } from '@/common/enums/TransportStopType';
import { SlotKind } from '@/common/enums/SlotKind';
import { PaymentTermKind } from '@/common/enums/PaymentTermKind';

@Component({
	selector: 'app-reservation-summary-documents',
	templateUrl: './reservation-summary-documents.component.html',
	styleUrls: ['./reservation-summary-documents.component.scss'],
	providers: [
		DatePipe,
		{
			provide: HTTP_INTERCEPTORS,
			useClass: RequestTrackerInterceptor,
			multi: true,
		},
		PricePipe,
	],
})
export class ReservationSummaryDocumentsComponent implements OnInit, OnDestroy {
	selectedDocumentName = 'reservation_confirm';
	reservation: Reservation;
	payments: Payment[];
	paymentTerm: PaymentsTerm[];
	documents: Document[] = [];

	displayedColumns: string[] = ['icon', 'name', 'date', 'author', 'download'];
	protected showProgressBar = false;

	constructor(
		readonly _service: ReservationService,
		protected api: DataBagService,
		protected dateService: DateService,
		protected store: ReservationSummaryStoreService,
		protected infobox: InfoboxService,
		protected reservationApiService: ReservationSummaryApiService,
		protected loader: LoaderService,
		protected pricePipe: PricePipe,
		protected datePipe: DatePipe,
		protected documentDownloader: DocumentDownloader,
		protected requestTracker: RequestTrackerService,
		protected dateBoundsPipe: DateBoundsPipe,
	) {}

	ngOnInit(): void {
		const { payment, paymentTerm, document } = this.store.getters();
		const { getDocuments } = this.reservationApiService.actions();
		this._service.loadDocumentsKinds();
		this._service.reservation$.subscribe((value: Reservation) => {
			if (value) {
				this.reservation = value;
				getDocuments(value.id);
			}
		});
		document().subscribe((value) => {
			if (value)
				this.documents = [
					this.findLatestDocument(value, 'reservation_confirm'),
					this.findLatestDocument(value, 'reservation_agreement'),
					this.findLatestDocument(value, 'voucher'),
				].filter((v) => v != undefined);
		});
		paymentTerm().subscribe((value) => {
			if (value) this.paymentTerm = value;
		});
		payment().subscribe((value) => {
			if (value) this.payments = value;
		});
	}

	ngOnDestroy(): void {
		this.documentDownloader.documentUnsubscribe$.next();
		this.documentDownloader.documentUnsubscribe$.complete();
	}

	protected trackByDocumentId(index: number, document: DocumentList): number {
		return document.id;
	}

	get documentToGenerate() {
		return this._service.documentKinds.filter(
			(value) => !value.displayName.includes('MAIL') && value.name !== 'reservation_cash_confirmation',
		);
	}

	get documentList() {
		const generatedDoc: DocumentList[] = this.documents?.map((v) => {
			return {
				id: v.id,
				name: this._service.convertDocumentName(v),
				createdAt: v.createdAt,
				createdBy: {
					id: v.createdBy?.id ?? null,
					firstName: v.createdBy?.firstName ?? '',
					lastName: v.createdBy?.lastName ?? '',
					email: null,
				},
				url: null,
			};
		});
		const attachedDoc: DocumentList[] = this.reservation?.multimedias.map((v) => {
			return {
				id: parseInt(v.id),
				name: v.name,
				createdAt: v.createdAt,
				createdBy: {
					id: null,
					firstName: null,
					lastName: null,
					email: v.createdBy,
				},
				url: v.url,
			};
		});
		return generatedDoc?.concat(attachedDoc);
	}

	private findLatestDocument(value: Document[], documentName: string) {
		return value
			?.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
			?.find((v) => v.name === documentName);
	}

	removeDuplicates(array: any[], conditionCallback: (currentElement: any, targetElement: any) => boolean): any[] {
		return array.filter((item, index, self) => {
			return index === self.findIndex((t) => conditionCallback(t, item));
		});
	}

	basicCondition(currentElement: number, targetElement: number): boolean {
		return currentElement === targetElement;
	}

	countVarQuantity(variableType) {
		const arr = [];
		this.reservation.offer
			.filter((v) => !CancellationStatuses.includes(v.status as Status))
			.forEach((item: Offer) => {
				if (!item.roomType) return;
				const foundVar = arr.find((value): boolean => value.id === item[variableType].id);
				if (foundVar) return foundVar.count++;
				arr.push({
					count: 1,
					id: item[variableType].id,
					name: item[variableType].name,
				});
			});
		return arr;
	}

	getTransportKind() {
		const arr = [];
		this.reservation.offer
			.filter((v) => !CancellationStatuses.includes(v.status as Status))
			.forEach((item: Offer) => {
				const foundVar = arr.find((value): boolean => value.kind === item.transportKind);
				if (foundVar) return;
				arr.push({
					kind: item.transportKind,
				});
			});
		return arr;
	}

	convertObjProduct(variableType) {
		const arr = [];
		this.reservation.offer
			.filter((v) => !CancellationStatuses.includes(v.status as Status))
			.forEach((item: Offer) => {
				item.product[variableType].forEach((obj) => {
					const foundVar = arr.find((value): boolean => value.id === obj.id);
					if (foundVar) return;
					arr.push({
						id: obj.id,
						name: obj.name,
					});
				});
			});
		return arr;
	}

	getTermTime() {
		let minDate: string;
		let maxDate: string;
		this.reservation.offer
			.filter((v) => !CancellationStatuses.includes(v.status as Status))
			.forEach((item: Offer) => {
				if (item.term.dateRange.lower < minDate || !minDate) {
					minDate = item.term.dateRange.lower;
				}

				if (item.term.dateRange.upper > maxDate || !maxDate) {
					maxDate = item.term.dateRange.upper;
				}
			});
		return {
			lower: this.dateBoundsPipe.transform(minDate, this.reservation.offer[0].term.dateRange.bounds, true),
			upper: this.dateBoundsPipe.transform(maxDate, this.reservation.offer[0].term.dateRange.bounds, false),
		};
	}

	convertSurchargeProgram(variableType) {
		const arr = [];
		this.reservation.offer
			.filter((v) => !CancellationStatuses.includes(v.status as Status))
			.forEach((item: Offer) => {
				const surchargeCurrency = this._service.currencies.find((v) => v.id === item.surchargeCurrency);
				if (!item[variableType].surchargeProgram || !item.surchargeFee || !surchargeCurrency) {
					return;
				}
				const foundVar = arr.find((value): boolean => value.id === item[variableType].surchargeProgram.id);
				if (foundVar) return foundVar.count++;
				arr.push({
					count: 1,
					id: item[variableType].surchargeProgram?.id,
					description: item[variableType].surchargeProgram?.description,
					fee: item.surchargeFee ? this.pricePipe.transform(item.surchargeFee) : 0,
					currency: surchargeCurrency?.code ?? null,
				});
			});
		return arr;
	}

	isCanceled(item) {
		return CancellationStatuses.includes(item?.status as Status);
	}

	setPassengers() {
		const passengers = [];
		this.reservation.offer.forEach((item: Offer) => {
			item.slot
				.filter((slot: Slot) => !this.isCanceled(slot))
				.forEach((slot: Slot) => {
					if (slot.userData) {
						const findedUserByPhone = slot.userData.userContacts.find((value) => value.phone);
						passengers.push({
							lastName: slot.userData.lastName,
							firstName: slot.userData.firstName,
							birthday: this.dateService.formatDateToGivenLocale(slot.userData.birth, 'pl-PL'),
							phone: findedUserByPhone ? findedUserByPhone.phone : '',
						});
					}
				});
		});
		return passengers;
	}

	convertOwnerOrganization(type: string) {
		const mainAddress = this.reservation?.[type]?.address.find((item) => item.kind.id === AddressKind.HEADQUATERS); //find HQ address;
		const phones = [];
		this.reservation?.[type]?.departments.forEach((item) => {
			item.departmentContacts.forEach((contact) => {
				if (contact?.phone) phones.push(contact?.phone);
			});
		});
		let bankAccount = this._service.incomingBankAccounts.find(
			(v) => v.organization === this.reservation?.[type]?.id && v.mainAccount,
		)?.number;
		bankAccount = bankAccount ? bankAccount : 'brak';
		return {
			name: this.reservation?.[type]?.name,
			street: mainAddress?.address,
			zipCode: mainAddress?.zipCode,
			city: mainAddress?.location.name,
			nip: this.reservation?.[type]?.nip,
			phones: phones,
			bankAccount: bankAccount,
			travelOrganizationGovernmentApproval: this.reservation?.[type]?.travelOrganizationGovernmentApproval,
		};
	}

	setLocation() {
		let country = null;
		let region = null;
		this.reservation.offer
			.filter((item: Offer) => !this.isCanceled(item))
			.forEach((item) => {
				item.product.location.forEach((value) => {
					country = this.findLocation(value, LocationKinds.COUNTRY);
					region = this.findLocation(value, LocationKinds.REGION);
				});
			});
		const foundLocations = { country: country, region: region };
		Object.keys(foundLocations).forEach((v) => {
			if (!foundLocations[v]) delete foundLocations[v];
		});
		return foundLocations;
	}

	getServices(reservation = this.reservation) {
		const services = [];
		reservation.offer.forEach((offer) => {
			offer.slot
				.filter((slot: Slot) => !this.isCanceled(slot))
				.forEach((slot) => {
					services.push(...slot.slotServices);
				});
		});
		const convertedServices = [];
		services.forEach((item) => {
			const foundVar = convertedServices.find((value): boolean => value.id === item.service.id);
			if (foundVar) {
				foundVar.price += foundVar.price / foundVar.count;
				foundVar.count++;
				return;
			}
			convertedServices.push({
				count: 1,
				id: item.service.id,
				price: item.price,
				name: item.service.name,
			});
		});
		return convertedServices;
	}

	get statusTime() {
		switch (this.reservation.status) {
			case Status.OPTION:
				return this.formatStatusTime(this.reservation.expirationTime);
			case Status.CONFIRMED:
				return this.formatStatusTime(this.reservation.confirmationTime);
			case Status.CANCELED_BY_CLIENT:
			case Status.CANCELED_OBJECT:
			case Status.CANCELED:
			case Status.CANCELED_TERM:
			case Status.CANCELED_WITHOUT_COST:
				return this.formatStatusTime(this.reservation.canceledAt);
			default:
				return '';
		}
	}

	get sumOfPayments(): number {
		let paymentBalance = 0;
		this.payments
			.filter((v) => v.status === PaymentStatus.COMPLETED)
			?.forEach((payment) => {
				paymentBalance += payment.amount;
			});
		return paymentBalance;
	}

	get productName() {
		return this.reservation.offer
			.filter((item: Offer) => !this.isCanceled(item))
			.map((item: Offer) => {
				return item.product.name;
			});
	}

	setParams(documentName, reservation = this.reservation) {
		switch (documentName) {
			case DocumentName.RESERVATION_CONFIRM:
				return {
					product: this.removeDuplicates(this.productName, this.basicCondition),
					board: this.countVarQuantity('board'),
					reservation: {
						id: reservation.id,
						fullPrice: reservation.totalPrice,
						adultPriceWithoutServices: this.calculateParticipant(SlotKind.ADULT, this.priceCondition),
						childrenPriceWithoutServices: this.calculateParticipant(SlotKind.CHILD, this.priceCondition),
						adultCount: this.calculateParticipant(SlotKind.ADULT, this.countCondition),
						childrenCount: this.calculateParticipant(SlotKind.CHILD, this.countCondition),
						status: reservation.status,
						reservationTime: this.dateService.formatDateToGivenLocale(
							this.dateService.apiToDateTime(reservation.reservationTime).date,
							'pl-PL',
						),
					},
					roomType: this.countVarQuantity('roomType'),
					dateRange: {
						lower: this.dateService.formatDateToGivenLocale(
							this.dateService.apiToDateTime(reservation.time.lower).date,
							'pl-PL',
						),
						upper: this.dateService.formatDateToGivenLocale(
							this.dateService.apiToDateTime(reservation.time.upper).date,
							'pl-PL',
						),
					},
					passenger: this.setPassengers(),
					organization: this.convertOwnerOrganization('organization'),
					seller: this.convertOwnerOrganization('seller'),
					transport: this.convertTransport(),
					location: this.setLocation(),
					serviceList: this.getServices(),
					commissionValue: reservation.commissionValue ?? 0,
					currentTime: this.dateService.formatDateToGivenLocale(this.dateService.now().date, 'pl-PL'),
					payments: this.setPayments(),
				};
			case DocumentName.VOUCHER:
				return {
					reservationId: reservation.id,
					hotel: this.removeDuplicates(this.productName, this.basicCondition),
					roomType: this.countVarQuantity('roomType'),
					organization: this.convertOwnerOrganization('seller'),
					board: this.countVarQuantity('board'),
					participantsList: this.setPassengers(),
					dateRange: {
						lower: this.convertApiDateToLocale(this.dateService.apiToDateTime(reservation.time.lower)),
						upper: this.convertApiDateToLocale(this.dateService.apiToDateTime(reservation.time.upper)),
					},
				};
			case DocumentName.RESERVATION_AGREEMENT:
				return {
					id: reservation.id,
					reservationStatus: {
						dateTime: this.statusTime,
						status: this.getStatus(reservation.status),
					},
					responsibleUser: reservation.responsibleUser,
					time: this.getTermTime(),
					reservationTime: reservation.reservationTime,
					totalPrice: reservation.totalPrice,
					surchargeProgram: this.convertSurchargeProgram('product'),
					hotel: this.removeDuplicates(
						reservation.offer
							.filter((v) => !CancellationStatuses.includes(v.status as Status))
							.map((item: Offer) => {
								return item.product.name;
							}),
						this.basicCondition,
					),
					organization: this.convertOwnerOrganization('organization'),
					seller: this.convertOwnerOrganization('seller'),
					board: this.countVarQuantity('board'),
					location: this.convertObjProduct('location'),
					transportKind: this.getTransportKind(),
					transportTerm: this.getTermTime(),
					roomType: this.countVarQuantity('roomType'),
					ownerUser: {
						firstName: reservation.ownerUser.firstName,
						lastName: reservation.ownerUser.lastName,
						birth: reservation.ownerUser.birth,
						email: reservation.ownerUser.userContacts.find((v) => v.email)?.email ?? null,
						phone: reservation.ownerUser.userContacts.find((v) => v.phone)?.phone ?? null,
					},
					offer: reservation.offer
						.filter((v) => !CancellationStatuses.includes(v.status as Status))
						.map((offer) => {
							return {
								additionalRoomSizeCharge:
									offer.additionalRoomSizeCharge !== 0 ? offer.additionalRoomSizeCharge : null,
								product: {
									name: offer.product.name,
									location: null,
									promotion: offer.promotion.length ? offer.promotion[0].catalog.name : '',
									board: offer.board.name,
								},
								roomType: offer.roomType ? offer.roomType.name : null,
								term: {
									lower: this.dateBoundsPipe.transform(
										offer.term.dateRange.lower,
										offer.term.dateRange.bounds,
										true,
									),
									upper: this.dateBoundsPipe.transform(
										offer.term.dateRange.upper,
										offer.term.dateRange.bounds,
										false,
									),
								},
								transportKind: offer.transportKind,
								insuranceName: offer.insurance.name,
								slot: offer.slot
									.filter((slot: Slot) => !this.isCanceled(slot))
									.map((slot) => {
										return {
											userData: slot.userData
												? {
														...slot.userData,
														phone:
															slot.userData.userContacts.find((v) => v.phone)?.phone ??
															null,
														email:
															slot.userData.userContacts.find((v) => v.email)?.email ??
															null,
													}
												: null,
											totalPrice: slot.totalPrice,
											promotionValue: slot.promotionValue,
											priceBoard: slot.priceBoard,
											transport: this.convertTransportPerSlot(slot),
											slotServices: slot.slotServices.map((value) => {
												return {
													name: value.service.name,
													price: value.price,
												};
											}),
										};
									}),
							};
						}),
					documentTime: {
						date: this.datePipe.transform(new Date().toString(), 'dd.MM.yyyy', 'pl-PL'),
						time: this.datePipe.transform(new Date().toString(), 'HH:mm', 'pl-PL'),
					},
					paymentTerms: this.setPaymentsTerms(),
				};
			case EmailType.RESERVATION_CANCEL_AGREEMENT:
				return {
					reservationId: reservation.id,
					ownerUser: {
						firstName: reservation.ownerUser.firstName,
						lastName: reservation.ownerUser.lastName,
						birth: reservation.ownerUser.birth,
						email: reservation.ownerUser.userContacts.find((v) => v.email)?.email ?? null,
						phone: reservation.ownerUser.userContacts.find((v) => v.phone)?.phone ?? null,
					},
					product: this.removeDuplicates(this.productName, this.basicCondition),
					totalPrice: reservation.totalPrice,
					paidByCustomer: this.sumOfPayments,
				};
			case EmailType.RESERVATION_CHANGED_AGREEMENT:
				return {
					reservationId: reservation.id,
					organization: this.convertOwnerOrganization('organization'),
					location: this.setLocation(),
					board: this.countVarQuantity('board'),
					product: this.removeDuplicates(this.productName, this.basicCondition),
					routeLink: this.getReservationSummaryLink(reservation.accessToken),
					time: reservation.time,
					paymentTerms: this.setPaymentsTerms(),
				};
			case EmailType.RESERVATION_CANCEL:
				return {
					reservationId: reservation.id,
					location: this.setLocation(),
					product: this.removeDuplicates(this.productName, this.basicCondition),
					time: reservation.time,
				};
			case EmailType.RESERVATION_PAYMENT_REMIND:
				return {
					reservationId: reservation.id,
					organization: this.convertOwnerOrganization('organization'),
					paymentTerms: this.setPaymentsTerms(),
					routeLink: this.getReservationSummaryLink(reservation.accessToken),
					commissionValue: reservation.commissionValue,
				};
			case EmailType.RESERVATION_CONFIRM_MAIL:
				return {
					expirationTime: reservation.expirationTime,
					reservationId: reservation.id,
					organization: this.convertOwnerOrganization('organization'),
					routeLink: this.getReservationSummaryLink(reservation.accessToken),
					paymentTerms: this.setPaymentsTerms(),
					location: this.setLocation(),
					board: this.countVarQuantity('board'),
					time: reservation.time,
					product: this.removeDuplicates(this.productName, this.basicCondition),
				};
			case EmailType.RESERVATION_CONFIRM_MAIL_FOR_AGENT:
				return {
					reservationId: reservation.id,
				};
			case EmailType.DOK_TO_SGL_MAIL:
			case EmailType.DOK_TO_SGL_MAIL_FOR_AGENT:
			case EmailType.DEPARTURE_CITY_WITHDRAWN:
			case EmailType.DEPARTURE_CITY_WITHDRAWN_FOR_AGENT:
				return {
					reservationId: reservation.id,
				};
			default:
				break;
		}
	}

	getReservationSummaryLink(accessToken): string {
		const domain = environment.production ? '' : 'dev.';
		return `https://${domain}oskar.com.pl/rezerwacja/podsumowanie/${accessToken}`;
	}

	setPaymentsTerms() {
		return this.getPaymentTermsByKind(PaymentTermKind.PAYMENT).map((obj) => {
			switch (obj.status) {
				case PaymentTermStatus.SETTLED:
					obj.status = 'Zapłacono';
					break;
				case PaymentTermStatus.UNSETTLED:
					obj.status = 'Do zapłaty';
					break;
				case PaymentTermStatus.PARTIALLY_SETTLED:
					obj.status = 'Częściowo opłacono';
					break;
				case PaymentTermStatus.OVERSETTLED:
					obj.status = 'Nadpłata';
					break;
			}
			delete obj.createdBy;
			delete obj.modifiedBy;
			return obj;
		});
	}

	async clickDocument(document: DocumentList) {
		if (document.url) return window.open(document.url, '_blank');
		this.showProgressBar = true;
		await this.documentDownloader.download(document.id);
		this.showProgressBar = false;
	}

	generateDocument() {
		const { getDocuments } = this.reservationApiService.actions();
		const selectedDocument: DocumentKind = this._service.documentKinds.find(
			(item: DocumentKind) => item.name === this.selectedDocumentName,
		);
		this.store.mutations().setReservation(this.reservation);
		this.requestTracker.additionalRequestFlag = false;
		const subscription = this.requestTracker.getRequestCountObservable().subscribe((count) => {
			if (count) return;
			if (!selectedDocument) return subscription.unsubscribe();
			this.requestTracker.additionalRequestFlag = true;
			try {
				const documentToSend: DocumentToSend = {
					name: selectedDocument.name,
					version: selectedDocument.version,
					params: this.setParams(this.selectedDocumentName),
					reservations: [this.reservation.id],
				};
				this.showProgressBar = true;
				this.api.post(
					`/reservation/${this.reservation.id}/documents/`,
					documentToSend,
					async (result, error) => {
						if (result) {
							await this.documentDownloader.download(result.id);
							this.showProgressBar = false;
							getDocuments(this.reservation.id);
						} else {
							this.showProgressBar = false;
						}
					},
				);
			} catch (error) {
				subscription.unsubscribe();
				throw new Error(error);
			}
			this.requestTracker.additionalRequestFlag = false;
			subscription.unsubscribe();
		});
	}

	attachDocuments(event: Event, kind = ''): void {
		const files = (event.target as HTMLInputElement).files;
		if (!files?.length) {
			return;
		}

		this.loader.show();
		const requests = [];
		Array.from(files).forEach((file: File) => requests.push(this.uploadDocument(file, kind)));
		Promise.all(requests)
			.then(() => {
				this._service.loadReservation(this.reservation.id);
			})
			.finally(() => this.loader.hide());
	}

	calculateParticipant(kind: SlotKind, condition: (slot: Slot[]) => number): number {
		return this.reservation.offer
			.map((res) => condition(res.slot.filter((v) => v.kind === kind)))
			.reduce((acc, ele) => acc + ele, 0);
	}

	priceCondition(slot: Slot[]): number {
		return slot.reduce((acc, ele) => acc + ele.totalPrice, 0);
	}

	countCondition(slot: Slot[]): number {
		return slot.filter((item: Slot) => !CancellationStatuses.includes(item?.status as Status)).length;
	}

	setPayments() {
		let price = 0;
		this.payments.forEach((val) => {
			price += val.amount;
		});
		const leftToPay = this.reservation.totalPrice - price;
		const sortedTerms = this.sortedTermObj();
		const advance = sortedTerms[0];
		return {
			leftToPay: leftToPay,
			sumOfPay: price,
			lastPaymentTerm: this.dateService.formatDateToGivenLocale(
				sortedTerms[sortedTerms.length - 1]?.term,
				'pl-PL',
			),
			advance: {
				term: this.dateService.formatDateToGivenLocale(advance.term, 'pl-PL'),
				value: advance.value,
			},
			priceWithoutAdvance: +this.reservation.totalPrice - advance.value,
		};
	}

	private stopCondition(currentElement: Slot, targetElement: Slot): boolean {
		return (
			currentElement.stopDeparture?.stop?.address === targetElement.stopDeparture?.stop?.address &&
			currentElement.stopReturn?.stop?.address === targetElement.stopReturn?.stop?.address
		);
	}

	private convertTransport(): DocumentConvertedTransport[] {
		const arr: DocumentConvertedTransport[] = [];
		this.reservation.offer.forEach((offer) => {
			const slots: Slot[] = this.removeDuplicates(offer.slot, this.stopCondition);
			slots.forEach((item) => {
				if (item.transportReturn && item.transportDeparture)
					arr.push({
						transportDeparture: this.setTransportBySlot(TransportStopType.STOP_DEPARTURE, item),
						transportReturn: this.setTransportBySlot(TransportStopType.STOP_RETURN, item),
					});
			});
		});
		return arr;
	}

	private convertTransportPerSlot(slot: Slot): DocumentConvertedTransport[] {
		return [
			{
				transportDeparture: this.setTransportBySlot(TransportStopType.STOP_DEPARTURE, slot),
				transportReturn: this.setTransportBySlot(TransportStopType.STOP_RETURN, slot),
			},
		];
	}

	private setTransportBySlot(transportType: TransportStopType, item: Slot): DocumentSlotTransport {
		if (item[transportType]) {
			const dateTime = this.dateService.apiToDateTime(item[transportType].timeDeparture);

			return {
				date: this.dateService.formatDateToGivenLocale(dateTime?.date, 'pl-PL'),
				time: dateTime?.time,
				address: `${item[transportType]?.stop.address} | ${item[transportType]?.stop.location.name}`,
			};
		}
		return null;
	}

	private findLocation(location: Location, locationKind: LocationKinds): string {
		if (location.locationKind === locationKind) return location.name;
		if (!location.parentLocation) return;
		return this.findLocation(location.parentLocation, locationKind);
	}

	private getPaymentTermsByKind(kind: PaymentTermKind): PaymentsTerm[] {
		return (this.paymentTerm ?? []).filter((term) => (term.kind ?? PaymentTermKind.PAYMENT) === kind);
	}

	private sortedTermObj(): SortedTermObj[] {
		const paymentTerms = this.getPaymentTermsByKind(PaymentTermKind.PAYMENT);
		if (!paymentTerms.length) {
			this.infobox.error('Brak terminów płatności!');
			return;
		}
		return paymentTerms.map((value) => {
			return { term: value.term, value: value.value };
		});
	}

	private convertApiDateToLocale(date: ApiDate): ApiDate {
		date.date = this.dateService.formatDateToGivenLocale(date.date, 'pl-PL');
		return date;
	}

	private getStatus(status: Status): string {
		return statusMappings[status] || '';
	}

	private formatStatusTime(statusTime: string): string {
		return statusTime ? this.datePipe.transform(statusTime, 'dd.MM.yyyy HH:mm', 'pl-PL') : '';
	}

	private uploadDocument(file: File, kind = ''): Promise<boolean> {
		return new Promise((resolve, reject) => {
			this.api.upload('/multimedia/file/', file, (result, error) => {
				if (result)
					this.api.post(
						`/reservation/${this.reservation.id}/multimedia/`,
						{ id: null, multimedia: result.id, kind },
						(res, error) => {
							if (res) resolve(true);
							else reject(error);
						},
					);
				else reject(error);
			});
		});
	}
}
